import { useEffect, useCallback, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { PushNotifications, type Token, type PushNotificationSchema } from '@capacitor/push-notifications'
import { useAuthStore } from '@/stores/auth.store'
import { notificationsApi } from '@/lib/notifications-api'

interface PushNotificationState {
  isSupported: boolean
  isRegistered: boolean
  permissionStatus: 'prompt' | 'granted' | 'denied' | 'unknown'
  error: string | null
}

// Store the current token for cleanup on logout
let currentToken: string | null = null

export function usePushNotifications() {
  const user = useAuthStore((state) => state.user)
  const accessToken = useAuthStore((state) => state.accessToken)

  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    isRegistered: false,
    permissionStatus: 'unknown',
    error: null,
  })

  // Check if push notifications are supported (native platforms only)
  const isSupported = Capacitor.isNativePlatform()

  /**
   * Request permission and register for push notifications
   */
  const register = useCallback(async () => {
    if (!isSupported) {
      setState((prev) => ({ ...prev, isSupported: false }))
      return
    }

    try {
      // Check current permission status
      const permStatus = await PushNotifications.checkPermissions()

      if (permStatus.receive === 'prompt') {
        // Request permission
        const result = await PushNotifications.requestPermissions()
        if (result.receive !== 'granted') {
          setState((prev) => ({
            ...prev,
            isSupported: true,
            permissionStatus: 'denied',
            error: 'Push notification permission denied',
          }))
          return
        }
      } else if (permStatus.receive === 'denied') {
        setState((prev) => ({
          ...prev,
          isSupported: true,
          permissionStatus: 'denied',
          error: 'Push notifications are disabled. Enable them in Settings.',
        }))
        return
      }

      // Register with APNs/FCM
      await PushNotifications.register()

      setState((prev) => ({
        ...prev,
        isSupported: true,
        permissionStatus: 'granted',
        error: null,
      }))
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isSupported: true,
        error: error instanceof Error ? error.message : 'Failed to register for push notifications',
      }))
    }
  }, [isSupported])

  /**
   * Unregister push token (call on logout)
   */
  const unregister = useCallback(async () => {
    if (currentToken) {
      try {
        await notificationsApi.unregisterToken(currentToken)
        currentToken = null
      } catch {
        // Ignore unregister errors - user is logging out anyway
      }
    }
    setState((prev) => ({ ...prev, isRegistered: false }))
  }, [])

  // Set up listeners when user is authenticated
  useEffect(() => {
    if (!isSupported || !user || !accessToken) {
      return
    }

    let registrationListener: Promise<{ remove: () => void }> | null = null
    let errorListener: Promise<{ remove: () => void }> | null = null
    let foregroundListener: Promise<{ remove: () => void }> | null = null
    let actionListener: Promise<{ remove: () => void }> | null = null

    const setupListeners = async () => {
      try {
        // Handle successful registration - receive the FCM/APNs token
        registrationListener = PushNotifications.addListener(
          'registration',
          async (token: Token) => {
            try {
              const platform = Capacitor.getPlatform() as 'ios' | 'android'
              await notificationsApi.registerToken(token.value, platform)
              currentToken = token.value
              setState((prev) => ({ ...prev, isRegistered: true, error: null }))
            } catch {
              setState((prev) => ({
                ...prev,
                error: 'Failed to register push token with server',
              }))
            }
          }
        )

        // Handle registration errors
        errorListener = PushNotifications.addListener('registrationError', (error) => {
          setState((prev) => ({
            ...prev,
            error: `Registration failed: ${error.error}`,
          }))
        })

        // Handle incoming notifications when app is in foreground
        foregroundListener = PushNotifications.addListener(
          'pushNotificationReceived',
          (_notification: PushNotificationSchema) => {
            // Optionally show an in-app notification or update UI
          }
        )

        // Handle notification tap (app opened from notification)
        actionListener = PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (action) => {
            // Navigate to relevant screen based on notification data
            const data = action.notification.data
            if (data?.type === 'update' && data?.updateId) {
              // Could navigate to the specific update
              // For now, user lands on timeline/dashboard which shows updates
            }
          }
        )

        // Initial registration
        await register()
      } catch (error) {
        // Firebase not configured or other push notification error
        // Don't crash the app, just log and disable push notifications
        console.warn('Push notifications unavailable:', error)
        setState((prev) => ({
          ...prev,
          isSupported: false,
          error: 'Push notifications not available on this device',
        }))
      }
    }

    setupListeners()

    // Cleanup listeners on unmount
    return () => {
      registrationListener?.then((l) => l.remove()).catch(() => {})
      errorListener?.then((l) => l.remove()).catch(() => {})
      foregroundListener?.then((l) => l.remove()).catch(() => {})
      actionListener?.then((l) => l.remove()).catch(() => {})
    }
  }, [isSupported, user, accessToken, register])

  return {
    ...state,
    register,
    unregister,
    currentToken,
  }
}

/**
 * Get the current push token (for logout cleanup)
 */
export function getCurrentPushToken(): string | null {
  return currentToken
}
